<template>
  <BottomSheet
    ref="sheet"
    fit
    close-position="end"
    title="Filters"
    data-cy="datatable-filter-sheet"
    @hide-sheet="emit('hide-sheet')"
  >
    <template #action>
      <button
        v-if="appliedCount > 0"
        type="button"
        class="text-sm font-medium text-gray-600"
        data-cy="datatable-filter-sheet-clear-all"
        @click="emit('clear-all')"
      >
        Clear All Filters
      </button>
    </template>
    <!-- pb-6 spaces the last filter from the footer's top border; the footer,
         not the body, carries the safe-area inset while it is present.
         Never give this a negative top margin to close the gap under the
         header: the wrapper is inside BottomSheet's `overflow-y-auto` body, so
         a negative margin starts the list above the scroll viewport and
         permanently clips the first label -- scrolling cannot bring it back. -->
    <div ref="slotHost" class="flex flex-col pb-6 divide-y divide-gray-100">
      <div
        v-for="(col, idx) in columns"
        :key="col.data ?? idx"
        class="flex flex-col gap-1 px-4 py-1.5"
        :data-cy="`${col.data ?? ''}_filter_sheet_row`"
      >
        <p class="text-sm font-medium text-gray-900">
          {{ columnLabel(col.title, col.data) }}
        </p>
        <div
          :class="[
            `${tableId}_header_${col.data ?? ''}`,
            'dt-filter-sheet-slot'
          ]"
        />
      </div>
    </div>
    <template #footer>
      <Button
        type="Primary"
        size="medium"
        class="w-full"
        data-cy="datatable-filter-sheet-show-results"
        @click="close"
      >
        {{ showResultsLabel }}
      </Button>
    </template>
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { trackMobileWebEvent } from "@/lib/pendo";

import BottomSheet from "@/components/Seranova/BottomSheet/BottomSheet.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import { columnLabel } from "@/mixins";

// The per-column filter slot host for a table's mobile filter sheet.
// Each row is an empty div carrying a column's `${tableId}_header_${col.data}`
// marker class -- the same marker `resolveHeaderTarget` resolves against -- so
// the parent's per-column SafeTeleport can move that column's real
// DatatableFilter instance into it. This component never renders filter
// content itself; it only publishes the slots and reports when they exist.
//
// The row list needs no scroll container of its own for a table with many
// columns: it renders inside BottomSheet's own body wrapper, which already
// owns `overflow-y-auto min-h-0` (BottomSheet.vue), so a tall row list simply
// scrolls within the sheet's fixed-height panel.
//
// The footer button closes through close() so the parent keeps one teardown.

interface SheetColumn {
  // Matches ColumnsEntity.data (DatatableV3.vue) rather than requiring it:
  // null only for a column DataTables was configured without a `data` key
  // at all, mirrored by DatatableSortSheet.vue's own SortColumn.data. The
  // template falls back to an empty string or the row index wherever this
  // is used as a key, so a null value never leaks in as the literal string
  // "null".
  data: string | null;
  title?: string;
}

const props = withDefaults(
  defineProps<{
    columns: SheetColumn[];
    tableId: string;
    // A plain count, not the filter objects: this component only needs to
    // decide whether the affordance shows, not what is applied.
    appliedCount?: number;
    // Null while the table has no count for the current result set.
    filteredCount?: number | null;
  }>(),
  { appliedCount: 0, filteredCount: null }
);

const showResultsLabel = computed(() => {
  if (props.filteredCount === null) return "Show results";
  if (props.filteredCount === 1) return "Show 1 result";
  return `Show ${props.filteredCount.toLocaleString("en-US")} results`;
});

const emit = defineEmits<{
  (e: "hide-sheet"): void;
  (e: "slots-ready", host: HTMLElement | null): void;
  // Routed straight into the parent's existing clearAllFilters -- one
  // implementation, two surfaces, no second clearing path.
  (e: "clear-all"): void;
}>();

const sheet = ref<InstanceType<typeof BottomSheet> | null>(null);
const slotHost = ref<HTMLElement | null>(null);

// `post` flush is load-bearing: it fires after the DOM update, so the parent
// only flips its teleport targets once the slots actually exist. A `pre`
// flush here would hand the parent a target Vue's `resolveTarget` cannot find
// yet, and Vue does not retry a failed Teleport target.
//
// Left non-immediate deliberately: `BottomSheet` mounts with `open = ref(false)`
// and flips it true in its own `onMounted`, so `slotHost` genuinely transitions
// null -> element after this component mounts. `immediate: true` would emit a
// `slots-ready(null)` before the sheet exists, which the parent would act on.
watch(
  slotHost,
  (el) => {
    emit("slots-ready", el ?? null);
  },
  { flush: "post" }
);

// This watcher only covers the host going away while this component stays
// alive -- what the real BottomSheet does after its leave transition unmounts
// the panel subtree. When this component itself unmounts, Vue's
// unmountComponent calls scope.stop() BEFORE unmounting the subtree, so the
// watcher above is already dead by the time the ref would null. Cover that
// path explicitly; `onBeforeUnmount` runs before `scope.stop()`, so this fires
// reliably, including under BottomSheetStub, which never removes its default
// slot.
onBeforeUnmount(() => {
  emit("slots-ready", null);
});

// Carries the body-rendered-menu contract this sheet's content needs: every
// column filter defaults `bodyRender` to true, so its serviceshift-ui Select
// teleports its menu to <body> at the hardcoded z-index: 21 of .select-options
// -- underneath this sheet's own z-[1003] panel, invisible. The fix is the
// same one Drawer.vue applies for its own bottom-sheet placement: a permanent
// CSS rule (below) gated on a body class this component owns.
//
// The CSS rule and the body class have different lifetimes. The rule is
// permanent -- Vite injects this component's non-scoped <style> at module
// load, and DatatableV3.vue imports this component statically, so the rule
// sits in the document on every page with a datatable whether or not a sheet
// is mounted, and it is never removed. The body class is the entire on/off
// switch, which is why its add/remove must be counted rather than paired 1:1
// with a mount: two DatatableV3 instances can each own a filter sheet, so an
// unguarded remove-on-unmount would strip the class while a second sheet's
// menus still need it. Parked on `window`, not module scope, because under
// Vitest each mount() of an SFC gets its own copy of a module-level `let`, so
// a module-scoped counter cannot express the two-instance case at all.
const BODY_MARKER_CLASS = "datatable-filter-sheet-open";
type FilterSheetDepth = { count: number };
const filterSheetDepth: FilterSheetDepth = ((
  window as unknown as { __seraDatatableFilterSheetDepth?: FilterSheetDepth }
).__seraDatatableFilterSheetDepth ??= { count: 0 });

onMounted(() => {
  filterSheetDepth.count += 1;
  if (filterSheetDepth.count === 1) {
    document.body.classList.add(BODY_MARKER_CLASS);
  }

  trackMobileWebEvent("datatable_filter_sheet_open", {
    surface: "datatable",
    table_id: props.tableId,
    column_count: props.columns.length
  });
});

// Done here, not in a hide-sheet handler: this component can also be torn
// down by the parent's responsiveActive watcher without any hide-sheet, and a
// marker left on <body> would lift every other body-rendered Select in the
// app.
onBeforeUnmount(() => {
  filterSheetDepth.count = Math.max(0, filterSheetDepth.count - 1);
  if (filterSheetDepth.count === 0) {
    document.body.classList.remove(BODY_MARKER_CLASS);
  }
});

const close = () => {
  sheet.value?.close();
};

defineExpose({ close });
</script>

<style lang="scss">
// 1004 is the next value in the band BottomSheet reserves for its default
// (non-`elevated`) sheet band -- panel z-[1003] / backdrop z-[1002] -- so the
// menu clears the panel while staying below SpinnerBackdrop at 1009. This
// clears the `sheet` band only: BottomSheet's `elevated` band sits at
// z-[10001]/z-[10000], and a future consumer passing `elevated` needs a
// higher lift than this, or its menu is re-buried under a lift that still
// reads as "fixed".
body.datatable-filter-sheet-open .select-options {
  z-index: 1004;
}
</style>
